<template>
  <div :style="cssVars">
    <div ref="editor" />
  </div>
</template>

<script lang="ts" setup>
import { bp600 } from '@/constants/breakpoints';
import Quill from 'quill';
import QuillBetterTable from 'quill-better-table';
import 'quill-better-table/dist/quill-better-table.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import 'quill/dist/quill.core.css';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { replaceTrailingSpaces } from '@/utils';

/*****************************************************************************
 * Types
 ****************************************************************************/
interface BetterTable {
  insertTable(rows: number, columns: number): void;
}

/*****************************************************************************
 * PROPS
 ****************************************************************************/
const props = withDefaults(
  defineProps<{
    height?: string;
    minHeight?: string;
    editable?: boolean;
    smallerHeaders?: boolean;
  }>(),
  {
    height: '400px',
    minHeight: undefined,
    editable: true,
    smallerHeaders: false,
  },
);

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['ready']);

/*****************************************************************************
 * SETUP
 ****************************************************************************/

let quillInstance: Quill | null = null;
const editor = ref();
const showToolbar = ref(true);
const editorBorderWidth = ref(1);

Quill.register(
  {
    'modules/better-table': QuillBetterTable,
  },
  true,
);

const setEditableAppearance = (editable: boolean) => {
  showToolbar.value = editable;
  editorBorderWidth.value = editable ? 1 : 0;
};

const cssBaseFontSize = computed(() => {
  if (props.smallerHeaders) {
    return (bp600 ? '0.6' : '0.7') + 'em';
  }
  return (bp600 ? '0.9' : '1') + 'em';
});

const cssVars = computed(() => {
  return {
    '--border': 'red',
    '--toolbar-display': showToolbar.value ? 'block' : 'none',
    '--editor-border-width': `${editorBorderWidth.value}px`,
    '--base-font-size': cssBaseFontSize.value,
    '--editor-height': props.height,
    '--editor-min-height': props.minHeight,
  };
});

const insertTable = () => {
  if (quillInstance) {
    const tableModule = quillInstance.getModule('better-table') as BetterTable;
    tableModule.insertTable(3, 3);
  }
};

watch(
  () => props.editable,
  (newValue) => {
    quillInstance?.enable(newValue);
    setEditableAppearance(newValue);
  },
);

onMounted(() => {
  if (editor.value) {
    const toolbarOptions = [
      [{ header: 1 }, { header: 2 }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }],
      ['table'],
    ];

    quillInstance = new Quill(editor.value, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: toolbarOptions,
          handlers: {
            table: insertTable,
          },
        },
        table: false,
        'better-table': {
          operationMenu: {
            items: {
              unmergeCells: {
                text: 'Unmerge cells name',
              },
            },
          },
        },
        keyboard: {
          bindings: QuillBetterTable.keyboardBindings,
        },
      },
    });
    quillInstance.enable(props.editable);

    // This matcher intercepts all pasted HTML elements (ELEMENT_NODE).
    // It modifies the Delta object before insertion by removing 'color' and 'background'
    // formatting attributes from each text operation.
    // This ensures that pasted content does not retain any text or background color styles,
    // while preserving other formatting like bold, italic, etc.
    quillInstance.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
      if (delta.ops)
        for (const op of delta.ops) {
          // eslint-disable-next-line sonarjs/nested-control-flow
          if (op.attributes) {
            delete op.attributes.color;
            delete op.attributes.background;
          }
        }
      return delta;
    });
    setEditableAppearance(props.editable);
  }
  emit('ready');
});

onBeforeUnmount(() => {
  quillInstance = null;
});

/*****************************************************************************
 * EXPOSE
 ****************************************************************************/
const setHTML = (html: string) => {
  if (quillInstance) {
    const delta = quillInstance.clipboard.convert({ html });
    quillInstance.setContents(delta, 'silent');
  }
};
const getHTML = () => {
  return quillInstance?.root.innerHTML;
};

const getText = (): string => {
  return quillInstance?.getText() ?? '';
};

const getPlainText = (): string => {
  const rawText = getText();
  if (!rawText) {
    return '';
  }

  return replaceTrailingSpaces(rawText);
};

defineExpose({
  setHTML,
  getHTML,
  getText,
  getPlainText,
});
</script>

<style scoped lang="scss">
@import 'styles/app.variables.scss';
@import 'styles/quasar.variables.scss';
:deep(.ql-toolbar) {
  display: var(--toolbar-display);
  border: var(--editor-border-width) solid nth($border-color, 1);
}
:deep(.ql-container) {
  border: var(--editor-border-width) solid nth($border-color, 1);
  font-size: 16px;
  height: var(--editor-height);
  min-height: var(--editor-min-height);
}

:deep(.ql-editor table td) {
  border: 1px solid nth(#8b9299, 1);
}

$toolbar-button-height: 27px;
$toolbar-button-width: 32px;

:deep(.ql-toolbar button) {
  height: $toolbar-button-height;
  width: $toolbar-button-width;
}
:deep(.ql-picker) {
  height: $toolbar-button-height;
  width: $toolbar-button-width;
}
:deep(.ql-picker-item) {
  color: black;
}

:deep(.ql-picker-label) {
  &:hover {
    color: black !important;
  }
}
:deep(.ql-editor ul li) {
  padding-left: 0;
}

:deep(.ql-editor ul .ql-indent-1) {
  padding-left: 25px;
}

:deep(.ql-editor ul .ql-indent-2) {
  padding-left: 50px;
}
:deep(.ql-editor ul .ql-indent-3) {
  padding-left: 75px;
}
:deep(.ql-editor ul .ql-indent-4) {
  padding-left: 100px;
}

:deep(.ql-editor h1) {
  font-size: calc(var(--base-font-size) * 2);
}

:deep(.ql-editor h2) {
  font-size: calc(var(--base-font-size) * 1.7);
}

:deep(.ql-editor h3) {
  font-size: calc(var(--base-font-size) * 1.6);
}

@media screen and (max-width: $bp600) {
  :deep(.ql-editor li, .ql-editor p) {
    font-size: calc(var(--base-font-size) * 1.5);
  }
}
</style>
